import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { ErrorBoundary, PageError } from "@/components/z6/ErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { FiltersProvider } from "@/context/FiltersContext";
import { TableProvider } from "@/context/TableContext";
import { Ocean } from "@/components/z6/Ocean";
import { FloatingHara } from "@/components/hara/PlateTray";
import WelcomePage from "@/pages/Welcome";
import MenuPage from "@/pages/Menus";
import BeveragesPage from "@/pages/Beverages";
import { PageWipe, PressRipples, SculptBG } from "@/components/avant/Avant";

// Heavy routes load on first visit; Bio-Q code is fetched only when it is opened.
const FullMenuPage = lazy(() => import("@/pages/FullMenu"));
const StaffPage = lazy(() => import("@/pages/StaffPage"));
const HaraPage = lazy(() => import("@/pages/Hara"));
const SummaryPage = lazy(() => import("@/pages/Summary"));
const AboutPage = lazy(() => import("@/pages/About"));
const RouteFallback = () => <div data-testid="route-loading" aria-busy="true" className="min-h-screen" />;

const AmbientLayer = () => (useLocation().pathname.startsWith("/staff") ? null : <Ocean />);
const SculptLayer = () => (useLocation().pathname.startsWith("/staff") ? null : <SculptBG />);

const useWakeLock = () => {
  useEffect(() => {
    let lock = null;
    const get = () => { if (document.visibilityState === "visible" && navigator.wakeLock) navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {}); };
    get();
    document.addEventListener("visibilitychange", get);
    return () => { document.removeEventListener("visibilitychange", get); lock?.release?.().catch(() => {}); };
  }, []);
};

function App() {
  useWakeLock();
  return (
    <div className="App">
      <MotionConfig reducedMotion="user">
        <FiltersProvider>
          <TableProvider>
            <ErrorBoundary fallback={<PageError />}>
            <BrowserRouter>
              <AmbientLayer />
              <SculptLayer />
              <PressRipples />
              <PageWipe>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<WelcomePage />} />
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/menu/all" element={<FullMenuPage />} />
                <Route path="/broths" element={<Navigate to="/menu" replace />} />
                <Route path="/beverages" element={<BeveragesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/bioq" element={<HaraPage />} />
                <Route path="/hara" element={<Navigate to="/bioq" replace />} />
                <Route path="/bio-q" element={<Navigate to="/bioq" replace />} />
                <Route path="/summary" element={<SummaryPage />} />
                <Route path="/staff" element={<StaffPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
              </PageWipe>
              <FloatingHara />
            </BrowserRouter>
            </ErrorBoundary>
          </TableProvider>
        </FiltersProvider>
        <Toaster position="top-center" />
      </MotionConfig>
    </div>
  );
}

export default App;
